import type { FlightOptions, MissionSimulationOptions } from '@trajectory/astro';
import { useCallback, useEffect, useRef } from 'react';
import { loadRun, play } from '@/lib/playbackClock';
import { useSettingsStore } from '@/stores/settingsStore';
import { useMissionStore } from '@/stores/missionStore';
import { useTelemetryStore } from '@/stores/telemetryStore';
import { useUiStore } from '@/stores/uiStore';
import type { SimWorkerRequest, SimWorkerResponse } from '@/workers/sim.worker';

/**
 * Owns the simulation Web Worker and exposes a single `run` function.
 * One worker per app instance — spinning up a fresh one per run would just
 * add startup latency for no benefit, since the worker holds no state
 * between runs beyond what each `run` message carries.
 */
export type BrowserSimulationOptions = FlightOptions | MissionSimulationOptions;

export function useSimWorker(): { run: (options: BrowserSimulationOptions) => void } {
  const workerRef = useRef<Worker | null>(null);
  const nextRequestIdRef = useRef(1);
  const activeRunRef = useRef<{
    requestId: number;
    missionId: string | null;
    missionVersion: number;
  } | null>(null);

  useEffect(() => {
    const worker = new Worker(new URL('../workers/sim.worker.ts', import.meta.url), { type: 'module' });
    worker.onmessage = (event: MessageEvent<SimWorkerResponse>) => {
      const message = event.data;
      const active = activeRunRef.current;
      if (
        !active ||
        message.requestId !== active.requestId ||
        active.missionId !== useMissionStore.getState().id ||
        active.missionVersion !== useMissionStore.getState().version
      ) {
        return;
      }
      if (message.type === 'ready') {
        useTelemetryStore.getState().setReady(message.series, message.events, message.summary);
        loadRun(message.summary.duration);
        if (!useSettingsStore.getState().reducedMotion) play();
      } else {
        useTelemetryStore.getState().setError(message.message);
        useUiStore.getState().notify({ kind: 'danger', title: 'Simulation failed', message: message.message });
      }
    };
    worker.onerror = (event) => {
      const active = activeRunRef.current;
      if (
        active &&
        (active.missionId !== useMissionStore.getState().id ||
          active.missionVersion !== useMissionStore.getState().version)
      )
        return;
      const message = event.message || 'Unknown worker error';
      useTelemetryStore.getState().setError(message);
      useUiStore.getState().notify({ kind: 'danger', title: 'Simulation worker crashed', message });
    };
    workerRef.current = worker;
    return () => {
      workerRef.current = null;
      activeRunRef.current = null;
      worker.terminate();
    };
  }, []);

  const run = useCallback((options: BrowserSimulationOptions) => {
    const worker = workerRef.current;
    if (!worker) {
      const message = 'The simulation worker is not ready. Reload the application and try again.';
      useTelemetryStore.getState().setError(message);
      useUiStore.getState().notify({
        kind: 'danger',
        title: 'Simulation unavailable',
        message,
      });
      return;
    }
    useTelemetryStore.getState().setRunning();
    const requestId = nextRequestIdRef.current++;
    activeRunRef.current = {
      requestId,
      missionId: useMissionStore.getState().id,
      missionVersion: useMissionStore.getState().version,
    };
    const request: SimWorkerRequest = { type: 'run', requestId, options };
    worker.postMessage(request);
  }, []);

  return { run };
}
